import { ADMIN_PROSPECTS_PAGE_SIZE } from './prospectsListRequest.js';

export const ADMIN_PROSPECT_ANALYSIS_LINK_CANDIDATES_PAGE_SIZE = ADMIN_PROSPECTS_PAGE_SIZE;
export const ADMIN_PROSPECT_ANALYSIS_ADDRESS_PREFIX_MIN = 2;

/** Always included server-side on create — not optional in the client payload. */
export const ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_ADDRESS = 'address';

/** Optional create transfer: latestMunicipalAssessment → municipalEvaluation. */
export const ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_MUNICIPAL = 'municipalAssessment';

/**
 * Optional create transfer: owner expense/tax lines from
 * latestOwnerProvidedFinancialSnapshot (CAD annual cents → dollar strings).
 * Never transfers annualRent / gross income.
 */
export const ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_OWNER_EXPENSES = 'ownerExpenseTaxes';

export const ADMIN_PROSPECT_ANALYSIS_OPTIONAL_TRANSFER_SECTIONS = Object.freeze([
  ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_MUNICIPAL,
  ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_OWNER_EXPENSES,
]);

/** Product defaults applied on create — not prospect-transferred data. */
export const ADMIN_PROSPECT_ANALYSIS_APPLICATION_DEFAULTS = Object.freeze({
  maintenance: '610',
  concierge: '365',
  managementRate: '5',
  vacancyRate: '3',
});

const OPTIONAL_TRANSFER_SECTION_SET = new Set(ADMIN_PROSPECT_ANALYSIS_OPTIONAL_TRANSFER_SECTIONS);

function clampPageSize(pageSize) {
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? Math.floor(size) : ADMIN_PROSPECT_ANALYSIS_LINK_CANDIDATES_PAGE_SIZE;
}

function optionalTrim(value) {
  if (value == null) return '';
  return String(value).trim();
}

function attachExpectedVersion(request, version) {
  if (version && typeof version === 'object' && Number.isInteger(version.s) && Number.isInteger(version.n)) {
    request.expectedUpdatedAtVersion = { s: version.s, n: version.n };
  } else if (typeof version === 'string' && version.trim()) {
    request.expectedUpdatedAt = version.trim();
  }
  return request;
}

function versionFingerprintPart(version) {
  if (version && typeof version === 'object' && Number.isInteger(version.s) && Number.isInteger(version.n)) {
    return { expectedUpdatedAtVersion: { s: version.s, n: version.n } };
  }
  if (typeof version === 'string' && version.trim()) {
    return { expectedUpdatedAt: version.trim() };
  }
  return {};
}

/**
 * Sorted unique optional transfer section ids for create requests.
 * `address` is never included — the server always transfers it.
 * @param {iterable<string> | null | undefined} selections
 * @returns {string[]}
 */
export function normalizeAdminProspectAnalysisTransferSelections(selections) {
  const out = new Set();
  if (selections == null) return [];
  for (const raw of selections) {
    const id = optionalTrim(raw);
    if (OPTIONAL_TRANSFER_SECTION_SET.has(id)) out.add(id);
  }
  return [...out].sort();
}

/**
 * Stable fingerprint for frozen create retries (excludes operationId).
 * @param {{
 *   prospectId: string,
 *   version?: { s: number, n: number } | string | null,
 *   transferSelections?: iterable<string> | null,
 * }} params
 */
export function fingerprintAdminProspectAnalysisConversionCreatePayload({
  prospectId,
  version = null,
  transferSelections = null,
} = {}) {
  return JSON.stringify({
    mode: 'create',
    prospectId: String(prospectId || '').trim(),
    ...versionFingerprintPart(version),
    transferSelections: normalizeAdminProspectAnalysisTransferSelections(transferSelections),
  });
}

/**
 * Stable fingerprint for frozen link retries (excludes operationId).
 * @param {{
 *   prospectId: string,
 *   propertyId: string,
 *   version?: { s: number, n: number } | string | null,
 * }} params
 */
export function fingerprintAdminProspectAnalysisConversionLinkPayload({
  prospectId,
  propertyId,
  version = null,
} = {}) {
  return JSON.stringify({
    mode: 'link',
    prospectId: String(prospectId || '').trim(),
    propertyId: String(propertyId || '').trim(),
    ...versionFingerprintPart(version),
  });
}

export function generateAdminProspectAnalysisConversionOperationId() {
  try {
    const webCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (webCrypto && typeof webCrypto.randomUUID === 'function') {
      return webCrypto.randomUUID.call(webCrypto);
    }
  } catch {
    // Some browsers expose randomUUID but throw when invoked (secure-context / binding issues).
  }
  return `analysis-conversion-op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Keeps one operationId + exact payload across transient retries for the same logical write.
 * Distinguishes preparing a new request from replaying a frozen uncertain attempt.
 * A frozen attempt is never silently replaced when the current form fingerprint changes.
 * Cleanup is operation-scoped so a stale completion cannot clear a newer attempt on the
 * same manager instance (or after reset + prepare of a different op).
 *
 * @returns {{
 *   reset: () => void,
 *   discard: () => void,
 *   discardIfOperation: (expectedOperationId: string | null | undefined) => boolean,
 *   hasAttempt: () => boolean,
 *   getAttemptPayload: () => object | null,
 *   getFrozenFingerprint: () => string | null,
 *   getOperationId: () => string | null,
 *   prepareNewAttempt: (params: {
 *     fingerprint: string,
 *     build: (operationId: string) => object,
 *     generateOperationId?: () => string,
 *   }) => { ok: true, payload: object, reused: boolean } | { ok: false, reason: 'attempt_locked', payload: object },
 *   retryFrozenAttempt: () => { ok: true, payload: object } | { ok: false, reason: 'no_attempt', payload: null },
 *   resolveAttemptPayload: (params: {
 *     fingerprint: string,
 *     build: (operationId: string) => object,
 *     generateOperationId?: () => string,
 *   }) => object,
 * }}
 */
export function createAdminProspectAnalysisConversionWriteAttempt() {
  let operationId = null;
  let attemptPayload = null;
  let frozenFingerprint = null;

  const clear = () => {
    operationId = null;
    attemptPayload = null;
    frozenFingerprint = null;
  };

  const currentOperationId = () => {
    if (operationId) return String(operationId);
    if (attemptPayload?.operationId != null) return String(attemptPayload.operationId);
    return null;
  };

  return {
    reset() {
      clear();
    },
    discard() {
      clear();
    },
    /**
     * Clear only when the frozen attempt is still the submitted operation.
     * Returns true when cleared; false when a different (or no) attempt is present.
     */
    discardIfOperation(expectedOperationId) {
      const expected = expectedOperationId == null ? '' : String(expectedOperationId).trim();
      if (!expected) return false;
      const current = currentOperationId();
      if (!current || current !== expected) return false;
      clear();
      return true;
    },
    hasAttempt() {
      return attemptPayload != null;
    },
    getAttemptPayload() {
      return attemptPayload;
    },
    getFrozenFingerprint() {
      return frozenFingerprint;
    },
    getOperationId() {
      return currentOperationId();
    },
    /**
     * Start a genuinely new write. Reuses only when the fingerprint still matches.
     * Refuses to replace a frozen attempt when the current form fingerprint differs.
     */
    prepareNewAttempt({
      fingerprint,
      build,
      generateOperationId = generateAdminProspectAnalysisConversionOperationId,
    }) {
      if (attemptPayload && frozenFingerprint === fingerprint) {
        return { ok: true, payload: attemptPayload, reused: true };
      }
      if (attemptPayload) {
        return { ok: false, reason: 'attempt_locked', payload: attemptPayload };
      }
      operationId = generateOperationId();
      attemptPayload = build(operationId);
      frozenFingerprint = fingerprint;
      return { ok: true, payload: attemptPayload, reused: false };
    },
    /**
     * Replay the exact stored payload and operationId — independent of current form state.
     */
    retryFrozenAttempt() {
      if (!attemptPayload) {
        return { ok: false, reason: 'no_attempt', payload: null };
      }
      return { ok: true, payload: attemptPayload };
    },
    /**
     * @deprecated Prefer prepareNewAttempt / retryFrozenAttempt.
     * Kept for callers that only prepare when no attempt is frozen.
     * Never rebuilds a different payload over a frozen attempt.
     */
    resolveAttemptPayload({
      fingerprint,
      build,
      generateOperationId = generateAdminProspectAnalysisConversionOperationId,
    }) {
      const prepared = this.prepareNewAttempt({ fingerprint, build, generateOperationId });
      if (prepared.ok) return prepared.payload;
      return prepared.payload;
    },
  };
}

/**
 * createAdminProspectAnalysisProperty payload.
 * Never sends uid/ownerUid — destination account is server-derived.
 * @param {{
 *   operationId: string,
 *   prospectId: string,
 *   version?: { s: number, n: number } | string | null,
 *   transferSelections?: iterable<string> | null,
 * }} params
 */
export function buildCreateAdminProspectAnalysisPropertyRequest({
  operationId,
  prospectId,
  version = null,
  transferSelections = null,
} = {}) {
  const request = attachExpectedVersion(
    {
      operationId: String(operationId ?? '').trim(),
      prospectId: String(prospectId || '').trim(),
    },
    version,
  );
  const normalized = normalizeAdminProspectAnalysisTransferSelections(transferSelections);
  if (normalized.length) {
    request.transferSelections = normalized;
  }
  return request;
}

/**
 * linkAdminProspectAnalysisProperty payload.
 * Never sends uid/ownerUid.
 * @param {{
 *   operationId: string,
 *   prospectId: string,
 *   propertyId: string,
 *   version?: { s: number, n: number } | string | null,
 * }} params
 */
export function buildLinkAdminProspectAnalysisPropertyRequest({
  operationId,
  prospectId,
  propertyId,
  version = null,
} = {}) {
  return attachExpectedVersion(
    {
      operationId: String(operationId ?? '').trim(),
      prospectId: String(prospectId || '').trim(),
      propertyId: String(propertyId || '').trim(),
    },
    version,
  );
}

/**
 * listAdminProspectAnalysisLinkCandidates request.
 * Opaque pageToken only. addressPrefix is trimmed; omit when empty.
 * @param {{
 *   pageSize?: number,
 *   pageToken?: string | null,
 *   addressPrefix?: string | null,
 * }} [params]
 */
export function buildListAdminProspectAnalysisLinkCandidatesRequest({
  pageSize = ADMIN_PROSPECT_ANALYSIS_LINK_CANDIDATES_PAGE_SIZE,
  pageToken,
  addressPrefix,
} = {}) {
  const request = {
    pageSize: clampPageSize(pageSize),
  };
  if (pageToken) request.pageToken = String(pageToken);
  const prefix = optionalTrim(addressPrefix);
  if (prefix) request.addressPrefix = prefix;
  return request;
}

/**
 * Resolve which address prefix to send for list/load-more.
 * Load more must reuse the last successful applied prefix; Search uses draft text.
 * @param {{
 *   append?: boolean,
 *   draftFilter?: string | null,
 *   appliedAddressPrefix?: string | null,
 *   minLength?: number,
 * }} params
 * @returns {{ prefix: string | undefined, effectiveDraft: string }}
 */
export function resolveAdminProspectAnalysisLinkCandidatesPrefix({
  append = false,
  draftFilter = '',
  appliedAddressPrefix = '',
  minLength = ADMIN_PROSPECT_ANALYSIS_ADDRESS_PREFIX_MIN,
} = {}) {
  const source = append ? optionalTrim(appliedAddressPrefix) : optionalTrim(draftFilter);
  if (!source || source.length < minLength) {
    return { prefix: undefined, effectiveDraft: source };
  }
  return { prefix: source, effectiveDraft: source };
}

function centsOrNull(value) {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/**
 * Client-side create preview mirrored from prospect detail (no invented prices).
 * Includes transfer-section availability for optional create checkboxes.
 * @param {Record<string, unknown> | null | undefined} prospect
 */
export function buildProspectAnalysisCreatePreview(prospect) {
  const src = prospect && typeof prospect === 'object' ? prospect : {};
  const assessment =
    src.latestMunicipalAssessment && typeof src.latestMunicipalAssessment === 'object'
      ? src.latestMunicipalAssessment
      : null;
  let municipalEvaluation = null;
  const assessmentCents = centsOrNull(assessment?.totalCents);
  if (assessmentCents != null) {
    const dollars = assessmentCents / 100;
    if (Number.isFinite(dollars)) {
      municipalEvaluation = Number.isInteger(dollars) ? String(dollars) : String(dollars);
    }
  }

  const financial =
    src.latestOwnerProvidedFinancialSnapshot && typeof src.latestOwnerProvidedFinancialSnapshot === 'object'
      ? src.latestOwnerProvidedFinancialSnapshot
      : null;
  const expenses =
    financial?.expenses && typeof financial.expenses === 'object' ? financial.expenses : null;

  const ownerExpenseFields = {
    municipalTaxes: centsOrNull(expenses?.municipalTaxesCents),
    schoolTaxes: centsOrNull(expenses?.schoolTaxesCents),
    insurance: centsOrNull(expenses?.insuranceCents),
    electricityHeating: centsOrNull(expenses?.heatingElectricityCents),
    otherExpenses: centsOrNull(expenses?.otherExpensesCents),
  };
  const ownerExpensePresent = Object.values(ownerExpenseFields).some((v) => v != null);
  const ownerExpenseMissing = Object.entries(ownerExpenseFields)
    .filter(([, v]) => v == null)
    .map(([key]) => key);

  const addressReady = Boolean(
    optionalTrim(src.address) &&
      optionalTrim(src.city) &&
      optionalTrim(src.province) &&
      optionalTrim(src.postalCode),
  );
  const addressMissing = [];
  if (!optionalTrim(src.address)) addressMissing.push('address');
  if (!optionalTrim(src.city)) addressMissing.push('city');
  if (!optionalTrim(src.province)) addressMissing.push('province');
  if (!optionalTrim(src.postalCode)) addressMissing.push('postalCode');

  return {
    address: typeof src.address === 'string' ? src.address : null,
    city: typeof src.city === 'string' ? src.city : null,
    province: typeof src.province === 'string' ? src.province : null,
    postalCode: typeof src.postalCode === 'string' ? src.postalCode : null,
    numberOfUnits: src.numberOfUnits ?? null,
    municipalEvaluation,
    municipalAssessmentCents: assessmentCents,
    askingPrice: null,
    purchasePrice: null,
    applicationDefaults: { ...ADMIN_PROSPECT_ANALYSIS_APPLICATION_DEFAULTS },
    transferSections: {
      address: {
        id: ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_ADDRESS,
        alwaysIncluded: true,
        available: addressReady,
        missing: addressMissing,
      },
      municipalAssessment: {
        id: ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_MUNICIPAL,
        alwaysIncluded: false,
        available: assessmentCents != null,
        missing: assessmentCents == null ? ['totalCents'] : [],
        totalCents: assessmentCents,
      },
      ownerExpenseTaxes: {
        id: ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_OWNER_EXPENSES,
        alwaysIncluded: false,
        available: ownerExpensePresent,
        missing: ownerExpenseMissing,
        fields: ownerExpenseFields,
      },
    },
  };
}
